import { InventoryType, ReceivingType } from '@/types';
import { createApi } from '@reduxjs/toolkit/query/react';
import { keepUnusedDataFor } from '../constants/appActions';
import { baseQueryWithReauth } from '../utils/prepHeaders';
import { ApiType } from '../utils/typeTransformer';
import { inventoryApi } from './inventoryApi';
import { procurementApi } from './procurementApi';

type ScopeQuery = {
  scope: ReceivingType.ReceivingScope;
  project_id?: number;
};

export type args = {
  getAllowedParts: ApiType<InventoryType.AllowedParts.Get>['args'];
  getExpectedReceiptsAll: ApiType<InventoryType.ExpectedReceipts.Get>['args'];
  postExpectedReceipts: ApiType<InventoryType.ExpectedReceipts.Post>['args'];
  deleteExpectedReceipt: ApiType<InventoryType.ExpectedReceipts.Delete>['args'];
  patchExpectedReceipt: ApiType<InventoryType.ExpectedReceipts.Patch>['args'];
  postOnHoldReceipts: ApiType<InventoryType.OnHoldReceipts.Post>['args'];
  getOnHoldReceiptsAll: ApiType<InventoryType.OnHoldReceipts.Get>['args'];
  deleteOnHoldReceipt: ApiType<InventoryType.OnHoldReceipts.Delete>['args'];
  patchOnHoldReceipt: ApiType<InventoryType.OnHoldReceipts.Patch>['args'];
  postConfirmedReceipts: ApiType<InventoryType.ConfirmedReceipts.Post>['args'];
  getRejectedReceiptsAll: ApiType<InventoryType.RejectedReceipts.GetRejectedReceiptsAll>['args'];
  exportRejectedInventory: ApiType<InventoryType.RejectedReceipts.ExportRejectedInventory>['args'];
  getItems: {
    query: ScopeQuery & { search?: string; active?: boolean };
  };
  postItem: {
    body: {
      scope: ReceivingType.ReceivingScope;
      project?: number | null;
      part?: number | null;
      item_code: string;
      name: string;
      description?: string;
      category?: string;
      unit_of_measure?: string;
      is_active?: boolean;
    };
  };
  postReconcileItem: {
    params: { item_id: number };
    body: { part_id: number };
  };
  getBatches: {
    query: ScopeQuery & { include_archived?: boolean };
  };
  postBatch: {
    body: ReceivingType.ReceivingBatchCreate;
  };
  postBatchReceive: {
    params: { batch_id: number };
    body: {
      lines: {
        expected_receipt_id: number;
        received_qty: number;
        reference?: string;
        notes?: string;
      }[];
    };
  };
  getPendingQc: { query: ScopeQuery };
  postBatchQc: {
    params: { batch_id: number };
    body: {
      receipt_id: number;
      qc_status: 'APPROVED' | 'REJECTED';
      quantity: number;
      qc_notes?: string;
    }[];
  };
  getRejected: { query: ScopeQuery };
  exportRejected: { query: ScopeQuery };
  postImport: {
    body: {
      file_url: string;
      file_name: string;
      content_type: string;
      size: number;
      scope: ReceivingType.ReceivingScope;
      project_id?: number | null;
    };
  };
  getImport: { params: { import_id: number } };
  postImportPreview: {
    params: { import_id: number };
    body: { column_mapping: Record<string, string> };
  };
  postImportCommit: {
    params: { import_id: number };
    body: Omit<ReceivingType.ReceivingImportCommit, 'import_id'>;
  };
};

export type responses = {
  getAllowedParts: ApiType<InventoryType.AllowedParts.Get>['response'];
  postExpectedReceipts: ApiType<InventoryType.ExpectedReceipts.Post>['response'];
  getExpectedReceiptsAll: ApiType<InventoryType.ExpectedReceipts.Get>['response'];
  patchExpectedReceipt: ApiType<InventoryType.ExpectedReceipts.Patch>['response'];
  postOnHoldReceipts: ApiType<InventoryType.OnHoldReceipts.Post>['response'];
  getOnHoldReceiptsAll: ApiType<InventoryType.OnHoldReceipts.Get>['response'];
  patchOnHoldReceipt: ApiType<InventoryType.OnHoldReceipts.Patch>['response'];
  postConfirmedReceipts: ApiType<InventoryType.ConfirmedReceipts.Post>['response'];
  getRejectedReceiptsAll: ApiType<InventoryType.RejectedReceipts.GetRejectedReceiptsAll>['response'];
  exportRejectedInventory: Blob;
  getItems: ReceivingType.ReceivingItem[];
  postItem: ReceivingType.ReceivingItem;
  postReconcileItem: ReceivingType.ReceivingItem;
  getBatches: ReceivingType.ReceivingBatch[];
  postBatch: ReceivingType.ReceivingBatch;
  postBatchReceive: ReceivingType.ReceivingReceipt[];
  getPendingQc: ReceivingType.ReceivingReceipt[];
  postBatchQc: { detail: string };
  getRejected: ApiType<InventoryType.RejectedReceipts.GetRejectedReceiptsAll>['response'];
  exportRejected: Blob;
  postImport: ReceivingType.ReceivingImport;
  getImport: ReceivingType.ReceivingImport;
  postImportPreview: ReceivingType.ReceivingImport;
  postImportCommit: ReceivingType.ReceivingBatch;
};

const refreshStockAfter = async (
  queryFulfilled: Promise<unknown>,
  dispatch: (action: unknown) => unknown,
) => {
  try {
    await queryFulfilled;
    dispatch(
      inventoryApi.util.invalidateTags([
        { type: 'Inventory', id: 'confirmed-receipts' },
        { type: 'Inventory', id: 'dashboard' },
      ]),
    );
  } catch {
    // The global API middleware reports the request error.
  }
};

const refreshSupplierMetricsAfter = async (
  queryFulfilled: Promise<unknown>,
  dispatch: (action: unknown) => unknown,
) => {
  try {
    await queryFulfilled;
    dispatch(procurementApi.util.invalidateTags([{ type: 'Procurement' }]));
  } catch {
    // The global API middleware reports the request error.
  }
};

export const receivingApi = createApi({
  reducerPath: 'receivingApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['Receiving'],
  endpoints: (builder) => ({
    getAllowedParts: builder.query<
      responses['getAllowedParts'],
      args['getAllowedParts']
    >({
      query: ({ params }) => ({
        url: `/production/projects/${params.project_id}/inventory/allowed-parts/`,
        method: 'GET',
      }),
      keepUnusedDataFor,
      providesTags: [{ type: 'Receiving', id: 'allowed-parts' }],
    }),

    postExpectedReceipts: builder.mutation<
      responses['postExpectedReceipts'],
      args['postExpectedReceipts']
    >({
      query: ({ params, body }) => ({
        url: `/production/projects/${params.project_id}/inventory/expected-receipts/`,
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: 'Receiving', id: 'expected-receipts' }],
      onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        void refreshStockAfter(queryFulfilled, dispatch);
      },
    }),

    getExpectedReceiptsAll: builder.query<
      responses['getExpectedReceiptsAll'],
      args['getExpectedReceiptsAll']
    >({
      query: ({ params, query }) => ({
        url: `/production/projects/${params.project_id}/inventory/expected-receipts/`,
        method: 'GET',
        params: query,
      }),
      keepUnusedDataFor,
      providesTags: [{ type: 'Receiving', id: 'expected-receipts' }],
    }),

    deleteExpectedReceipt: builder.mutation<
      void,
      args['deleteExpectedReceipt']
    >({
      query: ({ params }) => ({
        url: `/production/inventory/expected-receipts/${params.id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: [
        { type: 'Receiving', id: 'expected-receipts' },
        { type: 'Receiving', id: 'on-hold-receipts' },
      ],
      onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        void refreshStockAfter(queryFulfilled, dispatch);
      },
    }),

    patchExpectedReceipts: builder.mutation<
      responses['patchExpectedReceipt'],
      args['patchExpectedReceipt']
    >({
      query: ({ params, body }) => ({
        url: `/production/inventory/expected-receipts/${params.id}/`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: [{ type: 'Receiving', id: 'expected-receipts' }],
      onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        void refreshStockAfter(queryFulfilled, dispatch);
      },
    }),

    postOnHoldReceipts: builder.mutation<
      responses['postOnHoldReceipts'],
      args['postOnHoldReceipts']
    >({
      query: ({ params, body }) => ({
        url: `/production/projects/${params.project_id}/inventory/receipts/`,
        method: 'POST',
        body,
      }),
      invalidatesTags: [
        { type: 'Receiving', id: 'expected-receipts' },
        { type: 'Receiving', id: 'on-hold-receipts' },
      ],
      onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        void refreshStockAfter(queryFulfilled, dispatch);
      },
    }),

    getOnHoldReceiptsAll: builder.query<
      responses['getOnHoldReceiptsAll'],
      args['getOnHoldReceiptsAll']
    >({
      query: ({ params }) => ({
        url: `/production/projects/${params.project_id}/inventory/receipts/`,
        method: 'GET',
      }),
      keepUnusedDataFor,
      providesTags: [{ type: 'Receiving', id: 'on-hold-receipts' }],
    }),

    patchOnHoldReceipts: builder.mutation<
      responses['patchOnHoldReceipt'],
      args['patchOnHoldReceipt']
    >({
      query: ({ params, body }) => ({
        url: `/production/inventory/receipts/${params.receipt_id}/`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: [
        { type: 'Receiving', id: 'on-hold-receipts' },
        { type: 'Receiving', id: 'expected-receipts' },
      ],
      onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        void refreshStockAfter(queryFulfilled, dispatch);
      },
    }),

    deleteOnHoldReceipt: builder.mutation<void, args['deleteOnHoldReceipt']>({
      query: ({ params }) => ({
        url: `/production/inventory/receipts/${params.receipt_id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: [
        { type: 'Receiving', id: 'on-hold-receipts' },
        { type: 'Receiving', id: 'expected-receipts' },
        { type: 'Receiving', id: 'rejected-receipts' },
        { type: 'Receiving', id: 'batches' },
        { type: 'Receiving', id: 'pending-qc' },
        { type: 'Receiving', id: 'rejected' },
        { type: 'Receiving', id: 'items' },
      ],
      onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        void refreshStockAfter(queryFulfilled, dispatch);
      },
    }),

    postConfirmedReceipts: builder.mutation<
      responses['postConfirmedReceipts'],
      args['postConfirmedReceipts']
    >({
      query: ({ body }) => ({
        url: '/production/inventory/receipts/qc-action/',
        method: 'POST',
        body,
      }),
      invalidatesTags: [
        { type: 'Receiving', id: 'on-hold-receipts' },
        { type: 'Receiving', id: 'expected-receipts' },
        { type: 'Receiving', id: 'rejected-receipts' },
        { type: 'Receiving', id: 'batches' },
        { type: 'Receiving', id: 'pending-qc' },
        { type: 'Receiving', id: 'rejected' },
        { type: 'Receiving', id: 'items' },
      ],
      onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        void refreshStockAfter(queryFulfilled, dispatch);
        void refreshSupplierMetricsAfter(queryFulfilled, dispatch);
      },
    }),

    getRejectedReceiptsAll: builder.query<
      responses['getRejectedReceiptsAll'],
      args['getRejectedReceiptsAll']
    >({
      query: ({ params }) => ({
        url: `/production/projects/${params.project_id}/inventory/receipts/rejected/`,
        method: 'GET',
      }),
      keepUnusedDataFor,
      providesTags: [{ type: 'Receiving', id: 'rejected-receipts' }],
    }),

    exportRejectedInventory: builder.mutation<
      responses['exportRejectedInventory'],
      args['exportRejectedInventory']
    >({
      query: ({ params }) => ({
        url: `/production/projects/${params.project_id}/inventory/receipts/rejected/export/`,
        method: 'GET',
        responseHandler: async (response) => response.blob(),
      }),
    }),

    getItems: builder.query<responses['getItems'], args['getItems']>({
      query: ({ query }) => ({
        url: '/receiving/items/',
        method: 'GET',
        params: query,
      }),
      keepUnusedDataFor,
      providesTags: [{ type: 'Receiving', id: 'items' }],
    }),

    postItem: builder.mutation<responses['postItem'], args['postItem']>({
      query: ({ body }) => ({
        url: '/receiving/items/',
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: 'Receiving', id: 'items' }],
    }),

    postReconcileItem: builder.mutation<
      responses['postReconcileItem'],
      args['postReconcileItem']
    >({
      query: ({ params, body }) => ({
        url: `/receiving/items/${params.item_id}/reconcile/`,
        method: 'POST',
        body,
      }),
      invalidatesTags: [
        { type: 'Receiving', id: 'items' },
        { type: 'Receiving', id: 'batches' },
        { type: 'Receiving', id: 'expected-receipts' },
      ],
    }),

    getBatches: builder.query<responses['getBatches'], args['getBatches']>({
      query: ({ query }) => ({
        url: '/receiving/batches/',
        method: 'GET',
        params: query,
      }),
      keepUnusedDataFor,
      providesTags: [{ type: 'Receiving', id: 'batches' }],
    }),

    postBatch: builder.mutation<responses['postBatch'], args['postBatch']>({
      query: ({ body }) => ({
        url: '/receiving/batches/',
        method: 'POST',
        body,
      }),
      invalidatesTags: [
        { type: 'Receiving', id: 'batches' },
        { type: 'Receiving', id: 'expected-receipts' },
        { type: 'Receiving', id: 'on-hold-receipts' },
        { type: 'Receiving', id: 'pending-qc' },
        { type: 'Receiving', id: 'items' },
      ],
      onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        void refreshStockAfter(queryFulfilled, dispatch);
      },
    }),

    postBatchReceive: builder.mutation<
      responses['postBatchReceive'],
      args['postBatchReceive']
    >({
      query: ({ params, body }) => ({
        url: `/receiving/batches/${params.batch_id}/receive/`,
        method: 'POST',
        body,
      }),
      invalidatesTags: [
        { type: 'Receiving', id: 'batches' },
        { type: 'Receiving', id: 'expected-receipts' },
        { type: 'Receiving', id: 'on-hold-receipts' },
        { type: 'Receiving', id: 'pending-qc' },
        { type: 'Receiving', id: 'items' },
      ],
      onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        void refreshStockAfter(queryFulfilled, dispatch);
      },
    }),

    getPendingQc: builder.query<
      responses['getPendingQc'],
      args['getPendingQc']
    >({
      query: ({ query }) => ({
        url: '/receiving/receipts/pending-qc/',
        method: 'GET',
        params: query,
      }),
      providesTags: [{ type: 'Receiving', id: 'pending-qc' }],
    }),

    postBatchQc: builder.mutation<
      responses['postBatchQc'],
      args['postBatchQc']
    >({
      query: ({ params, body }) => ({
        url: `/receiving/batches/${params.batch_id}/qc-actions/`,
        method: 'POST',
        body,
      }),
      invalidatesTags: [
        { type: 'Receiving', id: 'batches' },
        { type: 'Receiving', id: 'pending-qc' },
        { type: 'Receiving', id: 'rejected' },
        { type: 'Receiving', id: 'items' },
        { type: 'Receiving', id: 'expected-receipts' },
        { type: 'Receiving', id: 'on-hold-receipts' },
        { type: 'Receiving', id: 'rejected-receipts' },
      ],
      onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        void refreshStockAfter(queryFulfilled, dispatch);
        void refreshSupplierMetricsAfter(queryFulfilled, dispatch);
      },
    }),

    getRejected: builder.query<responses['getRejected'], args['getRejected']>({
      query: ({ query }) => ({
        url: '/receiving/rejected/',
        method: 'GET',
        params: query,
      }),
      providesTags: [{ type: 'Receiving', id: 'rejected' }],
    }),

    exportRejected: builder.mutation<
      responses['exportRejected'],
      args['exportRejected']
    >({
      query: ({ query }) => ({
        url: '/receiving/rejected/export/',
        method: 'GET',
        params: query,
        responseHandler: async (response) => response.blob(),
      }),
    }),

    postImport: builder.mutation<responses['postImport'], args['postImport']>({
      query: ({ body }) => ({
        url: '/receiving/imports/',
        method: 'POST',
        body,
      }),
    }),

    getImport: builder.query<responses['getImport'], args['getImport']>({
      query: ({ params }) => ({
        url: `/receiving/imports/${params.import_id}/`,
        method: 'GET',
      }),
      providesTags: (_result, _error, { params }) => [
        { type: 'Receiving', id: `import-${params.import_id}` },
      ],
    }),

    postImportPreview: builder.mutation<
      responses['postImportPreview'],
      args['postImportPreview']
    >({
      query: ({ params, body }) => ({
        url: `/receiving/imports/${params.import_id}/preview/`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_result, _error, { params }) => [
        { type: 'Receiving', id: `import-${params.import_id}` },
      ],
    }),

    postImportCommit: builder.mutation<
      responses['postImportCommit'],
      args['postImportCommit']
    >({
      query: ({ params, body }) => ({
        url: `/receiving/imports/${params.import_id}/commit/`,
        method: 'POST',
        body,
      }),
      invalidatesTags: [
        { type: 'Receiving', id: 'batches' },
        { type: 'Receiving', id: 'expected-receipts' },
        { type: 'Receiving', id: 'on-hold-receipts' },
        { type: 'Receiving', id: 'items' },
        { type: 'Receiving', id: 'pending-qc' },
      ],
      onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        void refreshStockAfter(queryFulfilled, dispatch);
      },
    }),
  }),
});

export namespace ReceivingApi {
  export const Api = receivingApi;
  export type Args = args;
  export type Responses = responses;
}
